import { useEffect, useState } from 'react';
import './AboutUs.css';

const BOOKING_URL = 'https://calendar.app.google/nCF9xEsJKPQ3nfmr5';

const slideshowImages = [
  '/Images/img_1.png',
  '/Images/img_2.png',
  '/Images/img_3.png',
  '/Images/img_4.png',
  '/Images/img_5.png',
  '/Images/img_6.png',
];

const whoWeArePoints = [
  'We help families build stronger financial foundations through education, strategy, and long-term planning.',
  'We focus on protecting life, health, income, and future goals with practical financial guidance.',
  'We believe real wealth starts with understanding, trust, and a plan that fits each family.',
];

const whatWeDoCards = [
  {
    title: 'Our Mission',
    image: '/Images/img_5.png',
    text:
      'Our mission is to help individuals and families create a stronger financial future through education, protection, and long-term wealth-building strategies.',
  },
  {
    title: 'Our Team',
    image: '/Images/img_1.png',
    text:
      'We are building a team-driven environment centered on service, leadership, mentorship, and support for families and future business leaders.',
  },
  {
    title: 'Our Approach',
    image: '/Images/slide_2.jpeg',
    text:
      'We focus on simple, practical strategies that help improve cash flow, strengthen protection, reduce financial stress, and support long-term goals.',
  },
];

const sixSteps = [
  {
    title: 'Improve Cash Flow',
    image: '/Images/slide_2.jpeg',
    text:
      'We start by identifying expenses that can be reduced, eliminated, or optimized, then look for opportunities to increase revenue and improve financial flexibility.',
  },
  {
    title: 'Become Debt Free',
    image: '/Images/slide_1.jpeg',
    text:
      'We help clients create a practical debt-reduction strategy for mortgages, auto loans, credit cards, student loans, and other financial obligations.',
  },
  {
    title: 'Build Emergency Reserves',
    image: '/Images/slide_1.jpeg',
    text:
      'A strong financial plan includes accessible emergency savings, ideally covering 3 to 6 months of net income in reserve for unexpected situations.',
  },
  {
    title: 'Get Proper Protection',
    image: '/Images/slide_1.jpeg',
    text:
      'We review whether families have enough life insurance and the right kind of protection for life events, healthcare costs, and long-term needs.',
  },
  {
    title: 'Grow Long-Term Savings',
    image: '/Images/slide_1.jpeg',
    text:
      'We help clients understand long-term savings strategies that aim to outpace inflation, reduce taxes, and support future financial independence.',
  },
  {
    title: 'Protect Family Legacy',
    image: '/Images/slide_1.jpeg',
    text:
      'A successful plan should include wealth-transfer strategies that help reduce estate tax exposure and keep more hard-earned money in the family.',
  },
];

function AboutUs() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 3500);

    return () => clearInterval(intervalId);
  }, []);

  const openBookingPopup = () => {
    window.open(
      BOOKING_URL,
      'bookingPopup',
      'width=900,height=700,top=100,left=200,resizable=yes,scrollbars=yes'
    );
  };

  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="about-container about-hero-grid">
          <div className="about-hero-copy">
            <span className="about-eyebrow">About Us</span>

            <h1 className="about-hero-title">
              Creating Wealth <span>for Families</span>
            </h1>

            <p className="about-hero-text">
              We help individuals and families protect what matters, improve
              financial understanding, and build a stronger path toward lasting
              wealth and opportunity.
            </p>

            <p className="about-hero-text">
              Our mission is to make financial education, protection, and
              planning more accessible through practical guidance and personal
              support.
            </p>

            <div className="about-hero-actions">
              <button
                type="button"
                onClick={openBookingPopup}
                className="about-btn about-btn-primary"
              >
                Book a Free Consultation
              </button>

              <a
                href="https://bit.ly/2Z6BE07"
                target="_blank"
                rel="noreferrer"
                className="about-btn about-btn-secondary"
              >
                Contact Us
              </a>
            </div>
          </div>

          <div className="about-hero-card">
            <div className="about-hero-card-inner">
              <span className="about-card-label">Our Vision</span>
              <h2>Lead with education, service, and opportunity.</h2>
              <p>
                We believe strong financial foundations begin with knowledge,
                trust, and the right strategy for each family’s unique goals.
              </p>

              <div className="about-stat-list">
                <div className="about-stat-item">
                  <strong>Families First</strong>
                  <span>Guidance built around real-life financial goals.</span>
                </div>
                <div className="about-stat-item">
                  <strong>Practical Support</strong>
                  <span>Simple strategies people can understand and apply.</span>
                </div>
                <div className="about-stat-item">
                  <strong>Long-Term Growth</strong>
                  <span>Planning focused on stability, protection, and legacy.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section-light">
        <div className="about-container about-two-col">
          <div className="about-dark-box about-slideshow-shell">
            <div className="about-slideshow">
              {slideshowImages.map((image, index) => (
                <img
                  key={image}
                  src={image}
                  alt={`About slideshow ${index + 1}`}
                  className={`about-slide ${index === currentSlide ? 'active' : ''}`}
                />
              ))}

              <div className="about-slide-overlay" />

              <div className="about-slide-dots">
                {slideshowImages.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`about-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Show slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="about-light-content">
            <span className="about-section-tag about-tag-dark">Who We Are</span>
            <h2 className="about-section-title about-title-dark">
              A financial group focused on protection, growth, and legacy
            </h2>

            <p className="about-section-text about-copy-dark">
              We are committed to helping families build a stronger future
              through financial education, practical planning, and long-term
              wealth strategies.
            </p>

            <p className="about-section-text about-copy-dark">
              Our goal is to serve with clarity, integrity, and support so
              clients can make confident financial decisions for every stage of life.
            </p>

            <div className="about-who-list">
              {whoWeArePoints.map((point) => (
                <div key={point} className="about-who-item about-who-item-light">
                  <span className="about-who-icon" />
                  <p>{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="about-section-dark">
        <div className="about-container">
          <div className="about-section-head center">
            <span className="about-section-tag">What We Do</span>
            <h2 className="about-section-title about-title-light">
              The values behind our service
            </h2>
            <p className="about-section-text about-copy-light about-center-text">
              Our service is centered on guidance, education, and practical
              support for families and future business leaders.
            </p>
          </div>

          <div className="about-three-grid">
            {whatWeDoCards.map((card) => (
              <article key={card.title} className="about-card-light">
                <div className="about-card-image-wrap">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="about-card-image"
                  />
                </div>

                <div className="about-card-body">
                  <span className="about-gold-line" />
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section-light">
        <div className="about-container">
          <div className="about-section-head center">
            <span className="about-section-tag about-tag-dark">Financial Security</span>
            <h2 className="about-section-title about-title-dark">
              6 Steps to Financial Security – Maximize Your Wealth Growth
            </h2>
            <p className="about-section-text about-copy-dark about-center-text">
              Each step is designed to help families strengthen their financial
              position, reduce risk, and build long-term confidence.
            </p>
          </div>

          <div className="about-six-grid">
            {sixSteps.map((step, index) => (
              <article key={step.title} className="about-card-dark">
                <div className="about-step-image-wrap">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="about-step-image"
                  />
                  <div className="about-step-number">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                </div>

                <div className="about-card-body">
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section-dark">
        <div className="about-container about-two-col">
          <div className="about-card-light about-card-body">
            <span className="about-section-tag about-tag-dark">Business Opportunity</span>
            <h2 className="about-section-title about-title-dark">
              Build with the support of a strong team
            </h2>
            <p className="about-section-text about-copy-dark">
              We believe opportunity grows when people receive mentorship,
              support, and a clear path forward.
            </p>
            <p className="about-section-text about-copy-dark">
              This path is designed for people who want to help families while
              building leadership and creating a future of their own.
            </p>
          </div>

          <div className="about-card-light about-card-body">
            <h3 className="about-side-card-title">What guides us</h3>
            <ul className="about-guides-list">
              <li>Education before pressure.</li>
              <li>Families before transactions.</li>
              <li>Long-term thinking over short-term decisions.</li>
              <li>Leadership through service, discipline, and consistency.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="about-section-light">
        <div className="about-container">
          <div className="about-hiring-layout">
            <div className="about-card-dark about-card-body">
              <span className="about-section-tag">We Are Hiring</span>
              <h2 className="about-section-title about-title-light">
                Start Your Own Business With Full Training and Support
              </h2>
              <p className="about-section-text about-copy-light">
                If you are driven, coachable, and passionate about helping
                families, this could be the right opportunity to grow with us.
              </p>
              <p className="about-section-text about-copy-light">
                We offer a path for individuals who want to build skills,
                create income, and serve others with purpose.
              </p>

              <div className="about-hero-actions">
                <a href="/consultation" className="about-btn about-btn-primary">
                  Book a Zoom Session
                </a>

                <a
                  href="tel:917-346-6654"
                  target="_blank"
                  rel="noreferrer"
                  className="about-btn about-btn-dark-outline"
                >
                  Learn More
                </a>
              </div>
            </div>

            <div className="about-card-dark about-hiring-image-box">
              <img
                src="/Images/hiring.png"
                alt="We are hiring"
                className="about-hiring-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="about-container about-cta-card">
          <span className="about-section-tag">Let’s Connect</span>
          <h2 className="about-section-title">
            Book Your FREE 1-on-1 Online Appointment with a Financial Expert
          </h2>
          <p className="about-section-text">
            Schedule a consultation to talk about financial education,
            protection planning, or business opportunity.
          </p>

          <div className="about-hero-actions">
            <button
              type="button"
              onClick={openBookingPopup}
              className="about-btn about-btn-primary"
            >
              Schedule Now
            </button>
            <a href="tel:917-346-6654" className="about-btn about-btn-secondary">
              Call / Text
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default AboutUs;